import { ShieldAlert, Loader2 } from "lucide-react";
import { useEmergencyConfig, EMERGENCY_POSITIONS } from "@/lib/safety/useEmergencyConfig";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const COLOR_PRESETS: { label: string; value: string }[] = [
  { label: "Red (default)", value: "0 84% 60%" },
  { label: "Orange", value: "24 95% 55%" },
  { label: "Yellow", value: "45 95% 50%" },
  { label: "Magenta", value: "330 80% 55%" },
  { label: "Violet", value: "270 75% 60%" },
  { label: "Teal", value: "175 70% 42%" },
];

const ICON_PRESETS = [
  { label: "Shield alert (default)", value: "shield-alert" },
  { label: "Phone", value: "phone" },
  { label: "Heart pulse", value: "heart-pulse" },
  { label: "Life buoy", value: "life-buoy" },
];

const EmergencyButtonSettings = () => {
  const { config, loading, update } = useEmergencyConfig();

  if (loading || !config) {
    return (
      <p className="text-sm text-muted-foreground inline-flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </p>
    );
  }

  const apply = async (patch: Parameters<typeof update>[0]) => {
    const { error } = await update(patch);
    if (error) toast.error(error);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between rounded-xl border border-border px-4 py-3">
        <div>
          <p className="text-sm font-medium">Show Emergency Button</p>
          <p className="text-xs text-muted-foreground">
            Floating button is always available across the app while signed in.
          </p>
        </div>
        <Switch checked={config.enabled} onCheckedChange={(v) => apply({ enabled: v })} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="ebl">Label (for screen readers)</Label>
          <Input
            id="ebl"
            maxLength={32}
            value={config.label}
            onChange={(e) => apply({ label: e.target.value })}
            disabled={!config.enabled}
          />
        </div>
        <div>
          <Label>Position</Label>
          <Select
            value={config.position}
            onValueChange={(v) => apply({ position: v as typeof config.position })}
            disabled={!config.enabled}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EMERGENCY_POSITIONS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Color</Label>
          <Select
            value={config.color_hsl}
            onValueChange={(v) => apply({ color_hsl: v })}
            disabled={!config.enabled}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COLOR_PRESETS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="inline-block h-3 w-3 rounded-full"
                      style={{ backgroundColor: `hsl(${p.value})` }}
                    />
                    {p.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Icon</Label>
          <Select
            value={config.icon}
            onValueChange={(v) => apply({ icon: v })}
            disabled={!config.enabled}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ICON_PRESETS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {config.enabled && (
        <div className="rounded-xl border border-border bg-muted/30 p-4 flex items-center gap-4">
          <div
            className="h-12 w-12 rounded-full flex items-center justify-center shadow"
            style={{ backgroundColor: `hsl(${config.color_hsl})`, color: "hsl(var(--primary-foreground))" }}
            aria-hidden
          >
            <ShieldAlert className="h-5 w-5" />
          </div>
          <div className="text-xs text-muted-foreground">
            Preview. The button function is <span className="font-medium text-foreground">always</span> the
            same: log a safety event, capture location, and dial your locale's emergency number.
            Only the appearance is customizable.
          </div>
        </div>
      )}
    </div>
  );
};

export default EmergencyButtonSettings;
